import React, { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Send, History } from "lucide-react";

const TEMPLATES = {
  believe: {
    label: "\u201CBelieve Before We Sell\u201D + new content",
    audience: "employees",
    subject: "New required reading: \u201CBelieve Before We Sell\u201D + fresh content on the LMS",
    body: `Hello,

From your next login to the UniVicoustic Learning Academy, "Believe Before We Sell" — the Founders' Manifesto, a message from the Managing Director — will open first. Please read it end-to-end and confirm before continuing to your modules.

We've also added new learning content to the academy recently. Log in to explore the latest additions and keep your progress going.

— UniVicoustic Learning Team`,
  },
  new_content: {
    label: "New content published",
    audience: "employees",
    subject: "New content published on the UniVicoustic Learning Academy",
    body: `Hello,

We've just published new learning content on the UniVicoustic Learning Academy. Log in to explore it — we recommend going through it at your own pace.

— UniVicoustic Learning Team`,
  },
  blank: {
    label: "Blank (write your own)",
    audience: "employees",
    subject: "",
    body: "",
  },
};

const AUDIENCE_LABELS = { all: "All learners", employees: "Employees only", dealers: "Dealers only", custom: "Typed addresses" };

const parseEmails = (raw) => {
  const seen = new Set();
  const valid = [];
  const invalid = [];
  (raw || "").split(/[\s,;]+/).filter(Boolean).forEach((tok) => {
    const e = tok.trim().toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      if (!seen.has(e)) { seen.add(e); valid.push(e); }
    } else {
      invalid.push(tok);
    }
  });
  return { valid, invalid };
};

/** General-purpose admin composer: email any learner audience about anything. */
export const EmailComposerCard = ({ employeeCount = 0, dealerCount = 0 }) => {
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState("believe");
  const [audience, setAudience] = useState("employees");
  const [subject, setSubject] = useState(TEMPLATES.believe.subject);
  const [body, setBody] = useState(TEMPLATES.believe.body);
  const [sending, setSending] = useState(null); // "test" | "all" | null
  const [confirmStep, setConfirmStep] = useState(false);
  const [history, setHistory] = useState([]);
  const [customEmails, setCustomEmails] = useState("");

  const parsed = parseEmails(customEmails);
  const counts = {
    all: employeeCount + dealerCount,
    employees: employeeCount,
    dealers: dealerCount,
    custom: parsed.valid.length,
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/announcements/log?limit=6");
      setHistory(data.rows || []);
    } catch (_) { /* non-blocking */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const applyTemplate = (key) => {
    setTemplate(key);
    const t = TEMPLATES[key];
    setSubject(t.subject);
    setBody(t.body);
    setAudience(t.audience);
  };

  const send = async (testOnly) => {
    setSending(testOnly ? "test" : "all");
    try {
      const payload = { subject, body, audience, link_path: "/learn", cta_label: "Open the Learning Portal" };
      if (audience === "custom") payload.recipients = parsed.valid;
      if (testOnly) payload.test_email = getUser()?.email;
      const { data } = await api.post("/announcements/send-custom", payload);
      if (testOnly) {
        toast.success(`Test email sent to ${getUser()?.email}`);
      } else {
        toast.success(`Email sent to ${data.sent} of ${data.learner_count} recipients`);
        setOpen(false);
        loadHistory();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the email.");
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <Card className="p-5 border-slate-200 mb-10" data-testid="email-composer-card">
        <div className="flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center">
            <Mail size={20} className="text-blue-900" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">Email learners</div>
            <div className="text-sm text-slate-500">
              Send a custom announcement — new modules, manifesto updates, anything. Pick an audience, edit the message, send.
            </div>
          </div>
          <Button
            data-testid="email-composer-open"
            onClick={() => setOpen(true)}
            className="bg-blue-900 hover:bg-blue-800 text-white shrink-0"
          >
            <Mail size={15} className="mr-1.5" /> Compose email
          </Button>
        </div>

        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="email-history">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 inline-flex items-center gap-1.5">
              <History size={12} /> Recent sends
            </div>
            <div className="space-y-1.5">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 text-sm" data-testid={`email-history-row-${h.id}`}>
                  <span className="flex-1 min-w-0 truncate text-slate-700">{h.subject}</span>
                  <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {h.audience ? AUDIENCE_LABELS[h.audience] || h.audience : h.module_title || h.kind}
                  </span>
                  <span className="shrink-0 text-xs text-slate-500">{h.sent}/{h.learner_count} sent</span>
                  <span className="shrink-0 text-xs text-slate-400 w-24 text-right">
                    {h.sent_at ? new Date(h.sent_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Email learners</DialogTitle>
            <DialogDescription>
              Composes a branded email with an &ldquo;Open the Learning Portal&rdquo; button. Edit everything before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">Template</div>
                <Select value={template} onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TEMPLATES).map(([k, t]) => (
                      <SelectItem key={k} value={k} data-testid={`email-template-${k}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">Audience</div>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger data-testid="email-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employees" data-testid="email-audience-employees">Employees only ({counts.employees})</SelectItem>
                    <SelectItem value="dealers" data-testid="email-audience-dealers">Dealers only ({counts.dealers})</SelectItem>
                    <SelectItem value="all" data-testid="email-audience-all">All learners ({counts.all})</SelectItem>
                    <SelectItem value="custom" data-testid="email-audience-custom">Type email addresses…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {audience === "custom" && (
              <div>
                <Textarea
                  data-testid="email-custom-recipients"
                  placeholder="Type email addresses — separated by commas, spaces or new lines"
                  value={customEmails}
                  onChange={(e) => setCustomEmails(e.target.value)}
                  rows={3}
                  className="text-sm"
                />
                <div className="text-[11px] mt-1 flex flex-wrap gap-x-3">
                  <span className="text-slate-500" data-testid="email-custom-count">
                    {parsed.valid.length} valid address{parsed.valid.length === 1 ? "" : "es"}
                  </span>
                  {parsed.invalid.length > 0 && (
                    <span className="text-amber-700" data-testid="email-custom-invalid">
                      Ignored (not valid emails): {parsed.invalid.join(", ")}
                    </span>
                  )}
                </div>
              </div>
            )}
            <Input
              data-testid="email-subject-input"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            <Textarea
              data-testid="email-body-input"
              placeholder="Write your message… blank lines create new paragraphs."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={4000}
              className="text-sm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="email-send-test"
              disabled={!!sending || subject.length < 3 || body.length < 10}
              onClick={() => send(true)}
            >
              {sending === "test" ? "Sending…" : "Send me a test"}
            </Button>
            <Button
              data-testid="email-send-all"
              disabled={!!sending || subject.length < 3 || body.length < 10 || (audience === "custom" && parsed.valid.length === 0)}
              onClick={() => {
                if (!confirmStep) {
                  setConfirmStep(true);
                  setTimeout(() => setConfirmStep(false), 6000);
                  return;
                }
                setConfirmStep(false);
                send(false);
              }}
              className={`${confirmStep ? "bg-[#8f1f1d] hover:bg-[#7a1a18]" : "bg-blue-900 hover:bg-blue-800"} text-white`}
            >
              <Send size={14} className="mr-1.5" />
              {sending === "all"
                ? "Sending…"
                : confirmStep
                  ? `Really email ${counts[audience]} recipient${counts[audience] === 1 ? "" : "s"}?`
                  : audience === "custom"
                    ? `Send to ${counts.custom} address${counts.custom === 1 ? "" : "es"}`
                    : `Send to ${AUDIENCE_LABELS[audience].toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailComposerCard;
